import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { Link as RouterLink } from 'react-router-dom';

import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { Alert, Box, Button, Drawer, IconButton, Link, Stack, Tab, Tabs, Toolbar, Typography } from '@mui/material';

import { AiChatContext, GENERAL_CONVERSATION_ID } from './AiChatContext';
import AiChatConversationList from './AiChatConversationList';
import AiChatInput from './AiChatInput';
import AiChatMessageList from './AiChatMessageList';
import { isDepartmentSelectionQuestion } from './aiChatMessageUtils';
import { DEPARTMENT_OPTIONS, SUGGESTED_DRAFT_ACTIONS, SUGGESTED_QUESTIONS } from './chatModes';

const DEFAULT_DRAWER_WIDTH = 450;
const MIN_DRAWER_WIDTH = 320;
const MAX_DRAWER_WIDTH = Infinity;
const STORAGE_KEY = 'ai-chat-drawer-width';

function loadPersistedWidth(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = Number(stored);
      if (parsed >= MIN_DRAWER_WIDTH && parsed <= window.innerWidth) {
        return parsed;
      }
    }
  } catch {
    // localStorage unavailable
  }
  return DEFAULT_DRAWER_WIDTH;
}

export default function AiChatDrawer() {
  const chatContext = useContext(AiChatContext);
  const [drawerWidth, setDrawerWidth] = useState(loadPersistedWidth);
  const [activeTab, setActiveTab] = useState<'chat' | 'conversations'>('chat');
  const isDragging = useRef(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    function handleMouseMove(e: MouseEvent) {
      if (!isDragging.current) {
        return;
      }
      const newWidth = Math.min(MAX_DRAWER_WIDTH, Math.max(MIN_DRAWER_WIDTH, window.innerWidth - e.clientX));
      setDrawerWidth(newWidth);
    }

    function handleMouseUp() {
      if (!isDragging.current) {
        return;
      }
      isDragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setDrawerWidth((w) => {
        try {
          localStorage.setItem(STORAGE_KEY, String(w));
        } catch {
          // localStorage unavailable
        }
        return w;
      });
    }

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);
  const isOpen = chatContext?.isOpen ?? false;
  const conversations = chatContext?.conversations ?? {};
  const activeConversationId = chatContext?.activeConversationId ?? null;
  const activeConversation = chatContext?.activeConversation ?? null;
  const isLoading = chatContext?.isLoading ?? false;
  const error = chatContext?.error ?? null;
  const currentUrlCustomerId = chatContext?.currentUrlCustomerId ?? null;
  const closeChat = chatContext?.closeChat;
  const sendMessage = chatContext?.sendMessage;
  const clearChat = chatContext?.clearChat;
  const openChat = chatContext?.openChat;
  const switchConversation = chatContext?.switchConversation;
  const deleteConversation = chatContext?.deleteConversation;
  const showLauncher = !isOpen && !('Cypress' in window);

  const messages = activeConversation?.messages || [];
  const showSuggestions = messages.length === 0;
  const hasToolCallResults = messages.some((m) => m.toolCalls && m.toolCalls.length > 0);
  const showDraftActions = !showSuggestions && hasToolCallResults;
  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const pendingDepartmentSelection =
    !isLoading && lastMessage?.role === 'assistant' && isDepartmentSelectionQuestion(lastMessage.content);

  const conversationTitle = activeConversation
    ? activeConversation.id === GENERAL_CONVERSATION_ID
      ? 'General'
      : activeConversation.customerName || 'Customer'
    : 'AI Chat';

  const handleSelectConversation = useCallback(
    (conversationId: string) => {
      if (!switchConversation) {
        return;
      }
      switchConversation(conversationId);
      setActiveTab('chat');
    },
    [switchConversation],
  );

  const handleNewChat = useCallback(() => {
    if (!openChat) {
      return;
    }
    openChat();
    setActiveTab('chat');
  }, [openChat]);

  const handleCopyConversationRef = useCallback(async () => {
    if (!activeConversation?.conversationRef) {
      return;
    }

    try {
      await navigator.clipboard.writeText(activeConversation.conversationRef);
    } catch {
      // Clipboard access may be unavailable.
    }
  }, [activeConversation?.conversationRef]);

  if (!chatContext) {
    return null;
  }

  return (
    <>
      {showLauncher && (
        <Button
          variant="contained"
          size="small"
          startIcon={<SmartToyOutlinedIcon sx={{ fontSize: 16 }} />}
          onClick={() => openChat()}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 99999,
            fontSize: 12,
            py: 0.5,
            px: 1.5,
            borderRadius: 2,
            boxShadow: 3,
            minWidth: 'auto',
          }}
        >
          AI Chat
        </Button>
      )}

      <Drawer
        anchor="right"
        open={isOpen}
        onClose={closeChat}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: drawerWidth },
          },
        }}
      >
        <Box
          onMouseDown={handleMouseDown}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: 6,
            cursor: 'col-resize',
            '&:hover': { bgcolor: 'action.hover' },
            zIndex: 1,
          }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Toolbar
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              justifyContent: 'space-between',
              flexShrink: 0,
              minHeight: 48,
              gap: 1,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight="bold" noWrap>
                {activeTab === 'chat' && activeConversation ? (
                  <>
                    {activeConversation.customerId ? (
                      <Link component={RouterLink} to={`/users/${activeConversation.customerId}`} underline="hover">
                        {conversationTitle}
                      </Link>
                    ) : (
                      conversationTitle
                    )}
                  </>
                ) : (
                  'AI Chat'
                )}
              </Typography>
              {activeTab === 'chat' && activeConversation?.conversationRef && (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {activeConversation.conversationRef}
                </Typography>
              )}
            </Box>

            <Stack direction="row" spacing={0.5}>
              {activeTab === 'chat' && activeConversation?.conversationRef && (
                <IconButton
                  size="small"
                  onClick={handleCopyConversationRef}
                  title={`Copy conversation ref: ${activeConversation.conversationRef}`}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              )}
              {activeTab === 'chat' && activeConversation && (
                <IconButton
                  size="small"
                  onClick={clearChat}
                  disabled={messages.length === 0 && !error}
                  title="Clear messages"
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton size="small" onClick={closeChat} title="Close">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Toolbar>

          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="fullWidth"
            sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider', minHeight: 36 }}
          >
            <Tab label="Chat" value="chat" sx={{ minHeight: 36, py: 0 }} />
            <Tab label="Conversations" value="conversations" sx={{ minHeight: 36, py: 0 }} />
          </Tabs>

          {activeTab === 'conversations' ? (
            <AiChatConversationList
              conversations={conversations}
              activeConversationId={activeConversationId}
              currentUrlCustomerId={currentUrlCustomerId}
              onSelect={handleSelectConversation}
              onDelete={deleteConversation}
              onNewChat={handleNewChat}
            />
          ) : activeConversation ? (
            <>
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                <AiChatMessageList
                  messages={messages}
                  isLoading={isLoading}
                  orderId={activeConversation.orderId ?? null}
                  department={activeConversation.department ?? null}
                />
              </Box>

              {error && (
                <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
                  {error}
                </Alert>
              )}

              <AiChatInput
                onSend={sendMessage}
                isLoading={isLoading}
                suggestedQuestions={SUGGESTED_QUESTIONS}
                showSuggestions={showSuggestions}
                draftActions={SUGGESTED_DRAFT_ACTIONS}
                showDraftActions={showDraftActions}
                selectionOptions={pendingDepartmentSelection ? DEPARTMENT_OPTIONS : []}
                selectionLabel={pendingDepartmentSelection ? 'Choose your department' : null}
              />
            </>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
              <Typography color="text.secondary" variant="body2" textAlign="center">
                Select a conversation from the Conversations tab, or start a new one.
              </Typography>
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
}
